import { useState } from 'react';
import { Dialog } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { apiErrorMessage } from '@shared/api/client';
import {
  useAssociatePublicProfile,
  useVerifyAssociate,
} from '@shared/hooks/use-transaction-actions';
import type { Profession, Uuid } from '@shared/types/api';

function formatProfession(profession: Profession | null | undefined): string {
  if (!profession) return 'Associate Professional';
  switch (profession) {
    case 'CHARTERED_ACCOUNTANT':
      return 'Chartered Accountant (CA)';
    case 'LAWYER':
      return 'Lawyer / Legal Advocate';
    case 'COMPANY_SECRETARY':
      return 'Company Secretary (CS)';
    case 'COST_MANAGEMENT_ACCOUNTANT':
      return 'Cost & Management Accountant (CMA)';
    case 'TAX_CONSULTANT':
      return 'Tax Consultant';
    case 'DOCUMENT_WRITER':
      return 'Document Writer';
    case 'OTHER_DEGREE_HOLDER':
      return 'Degree Holder / Consultant';
    default:
      return String(profession).replace(/_/g, ' ');
  }
}

function StarIcons({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => {
        const fillPercent = Math.max(0, Math.min(1, rating - star + 1));
        return (
          <svg
            key={star}
            className="w-4 h-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#f59e0b"
            strokeWidth="1.5"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              fill={fillPercent > 0.5 ? '#f59e0b' : 'none'}
              d="M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z"
            />
          </svg>
        );
      })}
    </div>
  );
}

export function AssociateVerificationModal({
  open,
  onClose,
  transactionId,
}: {
  open: boolean;
  onClose: () => void;
  transactionId: Uuid;
}) {
  const {
    data: profile,
    isLoading,
    isError,
    error,
  } = useAssociatePublicProfile(transactionId, open);
  const verify = useVerifyAssociate(transactionId);
  const [rejectConfirmOpen, setRejectConfirmOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [reasonError, setReasonError] = useState<string | null>(null);

  if (!open) return null;

  const currentRejections = profile?.associateRejectionCount ?? 0;
  const remainingChanges = Math.max(0, 2 - currentRejections);

  function handleContinue() {
    verify.mutate(
      { action: 'CONTINUE' },
      {
        onSuccess: () => {
          onClose();
        },
      },
    );
  }

  function handleRejectSubmit() {
    setReasonError(null);
    const trimmedReason = rejectReason.trim();
    if (!trimmedReason) {
      setReasonError('Reason for changing associate is required');
      return;
    }
    verify.mutate(
      { action: 'REJECT', reason: trimmedReason },
      {
        onSuccess: () => {
          setRejectConfirmOpen(false);
          setRejectReason('');
          onClose();
        },
      },
    );
  }

  return (
    <Dialog open={open} onClose={onClose} title="Verify Assigned Associate" className="max-w-lg">
      <div className="space-y-4 py-1 text-sm text-gray-700">
        {/* Change budget indicator */}
        <div className="flex items-center justify-between rounded-lg bg-amber-50 border border-amber-200 px-3.5 py-2 text-xs text-amber-900">
          <span className="font-medium">Associate Changes Policy</span>
          <span className="font-semibold text-amber-800 bg-amber-100 px-2 py-0.5 rounded">
            {remainingChanges} of 2 changes remaining
          </span>
        </div>

        {isLoading ? (
          <div className="py-8 text-center text-sm text-gray-500">Loading associate profile...</div>
        ) : isError || !profile ? (
          <div className="py-6 text-center text-sm text-red-600">
            Failed to load associate details: {apiErrorMessage(error, 'No associate data')}
          </div>
        ) : (
          <div className="space-y-5">
            {/* Main profile identity card */}
            <div className="flex items-start gap-4 p-4 rounded-xl border border-gray-200 bg-gray-50/70 shadow-xs">
              <div className="relative h-16 w-16 shrink-0 rounded-full overflow-hidden bg-brand-100 border border-brand-200 flex items-center justify-center shadow-xs">
                {profile.profilePictureUrl ? (
                  <img
                    src={profile.profilePictureUrl}
                    alt={profile.firstName ?? 'Associate'}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <span className="text-xl font-bold text-brand-800 uppercase">
                    {profile.firstName?.slice(0, 1) ?? 'A'}
                    {profile.lastName?.slice(0, 1) ?? ''}
                  </span>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-base font-bold text-gray-900 truncate">
                    {[profile.firstName, profile.lastName].filter(Boolean).join(' ') ||
                      'Assigned Associate'}
                  </h3>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-brand-100 text-brand-800 border border-brand-200">
                    {formatProfession(profile.profession)}
                  </span>
                </div>

                <div className="mt-2 space-y-1 text-xs text-gray-600">
                  {profile.associateId && (
                    <div className="flex items-center gap-1.5">
                      <span className="font-medium text-gray-500">Associate ID:</span>
                      <span className="font-mono text-gray-900 font-semibold">
                        {profile.associateId}
                      </span>
                    </div>
                  )}
                  {profile.email && (
                    <div className="flex items-center gap-1.5">
                      <span className="font-medium text-gray-500">Email:</span>
                      <span className="text-gray-900 font-medium truncate">{profile.email}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Rating & reviews section */}
            <div className="rounded-xl border border-gray-200 p-4 bg-white space-y-3">
              <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                <div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold text-gray-900">
                      {profile.rating.totalReviews > 0 ? profile.rating.average.toFixed(1) : '0.0'}
                    </span>
                    <StarIcons
                      rating={profile.rating.totalReviews > 0 ? profile.rating.average : 0}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Based on {profile.rating.totalReviews} verified client reviews
                  </p>
                </div>
                <div className="text-right">
                  <span className="text-xs font-semibold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-md">
                    Verified Associate
                  </span>
                </div>
              </div>

              {/* Client reviews list */}
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                  Client Feedback & Reviews
                </h4>
                {profile.reviews.length === 0 ? (
                  <p className="text-xs text-gray-400 italic py-2">
                    No client reviews posted yet. This associate is verified by EcRegi Admin.
                  </p>
                ) : (
                  <div className="max-h-40 overflow-y-auto space-y-2 pr-1 divide-y divide-gray-100">
                    {profile.reviews.map((rev, idx) => (
                      <div key={idx} className="pt-2 first:pt-0">
                        <div className="flex items-center justify-between text-xs">
                          <span className="font-semibold text-gray-900">{rev.clientName}</span>
                          <div className="flex items-center gap-1">
                            <StarIcons rating={rev.rating} />
                            <span className="text-[10px] text-gray-400">
                              {rev.completedAt
                                ? new Date(rev.completedAt).toLocaleDateString()
                                : ''}
                            </span>
                          </div>
                        </div>
                        {rev.feedbackDescription ? (
                          <p className="text-xs text-gray-600 mt-1 italic break-words">
                            "{rev.feedbackDescription}"
                          </p>
                        ) : (
                          <p className="text-[11px] text-gray-400 mt-0.5">
                            Rated {rev.rating} stars
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Error display */}
            {verify.isError && (
              <div className="rounded-md border border-red-200 bg-red-50 p-2.5 text-xs text-red-700 font-medium">
                {apiErrorMessage(verify.error)}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Verification Action Buttons */}
      {!rejectConfirmOpen ? (
        <div className="flex flex-col sm:flex-row items-center justify-end gap-2.5 pt-3 border-t border-gray-100">
          <Button
            type="button"
            variant="outline"
            onClick={() => setRejectConfirmOpen(true)}
            disabled={verify.isPending || isLoading}
            className="w-full sm:w-auto text-xs text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700 font-medium"
          >
            Cancel / Change Associate
          </Button>

          <Button
            type="button"
            onClick={handleContinue}
            disabled={verify.isPending || isLoading}
            className="w-full sm:w-auto text-xs bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-4 shadow-sm"
          >
            {verify.isPending ? 'Verifying...' : 'Continue with Associate'}
          </Button>
        </div>
      ) : (
        /* Reject / Change Associate confirmation box */
        <div className="mt-3 p-3.5 rounded-lg border border-red-200 bg-red-50/60 space-y-3">
          <h4 className="text-xs font-bold text-red-900">Confirm Associate Cancellation</h4>
          <p className="text-xs text-red-700 leading-relaxed">
            {remainingChanges === 1 ? (
              <span>
                This will use <strong>1 of your 2 allowed associate changes</strong>. Admin will
                assign a new associate for this service request.
              </span>
            ) : remainingChanges <= 0 ? (
              <span className="font-bold">
                Warning: You have reached 2 associate changes. Requesting another change will close
                this service request! You can then start a fresh request.
              </span>
            ) : (
              <span>
                Are you sure you want to change this associate? (Changes remaining:{' '}
                {remainingChanges}/2)
              </span>
            )}
          </p>

          <input
            type="text"
            value={rejectReason}
            onChange={(e) => {
              setRejectReason(e.target.value);
              if (reasonError) setReasonError(null);
            }}
            placeholder="Reason for changing associate (required)..."
            className="w-full rounded border border-red-300 px-3 py-1.5 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-red-500"
          />
          {reasonError ? (
            <p className="text-xs font-medium text-red-600 mt-1">{reasonError}</p>
          ) : null}

          <div className="flex items-center justify-end gap-2 pt-1">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setRejectConfirmOpen(false)}
              disabled={verify.isPending}
              className="text-xs h-8 text-gray-600"
            >
              Back
            </Button>
            <Button
              type="button"
              onClick={handleRejectSubmit}
              disabled={verify.isPending}
              className="text-xs h-8 bg-red-600 hover:bg-red-700 text-white font-medium"
            >
              {verify.isPending ? 'Processing...' : 'Confirm Cancellation'}
            </Button>
          </div>
        </div>
      )}
    </Dialog>
  );
}
